import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Doc, Reply, ReplyStatus, UserType } from '@app/models';
import { Button } from 'components/ui/button';
import { Info, Loader, Paper } from '@app/components';
import { currencyFormat, dateFormat } from '@app/helpers';
import {
  acceptReply,
  declineReply,
  getContracts,
  getReplyById,
  pinnedReply,
  unpinnedReply,
} from '@app/api';
import { AuthorSection } from '../index';
import { useNotification, useUserContext } from '@app/providers';
import {
  IconCheckCircle16,
  IconCheckCircle24,
  IconCloseCircle16,
  IconCloseCircle24,
  IconFlag,
} from '@app/icons';
import {
  ModalAddPartner,
  ModalConfirm,
  ModalContract,
  ModalReport,
} from '@app/modals';
import { useNavigate } from 'react-router-dom';
import Chat from '../Chat';
import { cn } from 'lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface Props {
  reply: Reply;
  onUpdate: (reply: Reply) => void;
}

function ReplyDetail(props: Props) {
  const { reply: propsReply, onUpdate } = props;
  const { user, company } = useUserContext();
  const { showNotification } = useNotification();
  const [pending, setPending] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [accepting, setAccepting] = useState<boolean>(false);
  const [declining, setDeclining] = useState<boolean>(false);
  const [pining, setPining] = useState<boolean>(false);
  const [reply, setReply] = useState<Reply | null>(null);
  const [hasContract, setHasContract] = useState<boolean>(false);
  const [reportModalVisible, setReportModalVisible] = useState<boolean>(false);
  const [createContractModalVisible, setCreateContractModalVisible] =
    useState<boolean>(false);
  const [modalContractVisible, setModalContractVisible] =
    useState<boolean>(false);
  const [contract, setContract] = useState<Doc | null>(null);
  const navigate = useNavigate();
  const [modalConfirmVisible, setModalConfirmVisible] =
    useState<boolean>(false);

  // Mobile collapsible states
  const [isInfoOpen, setIsInfoOpen] = useState<boolean>(false);
  const [isRequirementsOpen, setIsRequirementsOpen] = useState<boolean>(false);
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false);

  const getData = useCallback(async () => {
    try {
      const response = await getReplyById(propsReply.id);
      const contractsParams: Partial<Doc> = {};

      if (user.type === UserType.ENTITY) {
        contractsParams.companyId = company!.id;
      } else {
        contractsParams.ownerId = user.id;
      }

      if (!!response.companyId) {
        contractsParams.recipientCompanyId = response.companyId;
      } else {
        contractsParams.recipientId = response.ownerId;
      }

      if (!!response.tenderId) {
        contractsParams.tenderId = response.tenderId;
      }

      const contracts = await getContracts({
        page: 1,
        perPage: 1,
        ...contractsParams,
      });

      if (contracts.data.length > 0) {
        setHasContract(true);
      }

      setReply(response);
      setError(false);
      setPending(false);
    } catch (e) {
      setError(true);
      setPending(false);
    }
  }, [company, propsReply.id, user.id, user.type]);

  const onClickPinned = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      try {
        e.preventDefault();
        e.stopPropagation();

        setPining(true);

        const response = await pinnedReply(propsReply.id);

        onUpdate(response);
        setReply(response);

        setPining(false);

        showNotification({
          message: 'Отклик помечен',
          variant: 'success',
        });
      } catch (e) {
        setPining(false);
      }
    },
    [onUpdate, propsReply.id, showNotification]
  );

  const onClickUnpinned = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      try {
        e.preventDefault();
        e.stopPropagation();

        setPining(true);

        const response = await unpinnedReply(propsReply.id);

        onUpdate(response);
        setReply(response);

        setPining(false);

        showNotification({
          message: 'Отклик откреплен',
          variant: 'success',
        });
      } catch (e) {
        setPining(false);
      }
    },
    [onUpdate, propsReply.id, showNotification]
  );

  const onClickDecline = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      try {
        e.preventDefault();
        e.stopPropagation();

        setDeclining(true);

        const response = await declineReply(propsReply.id);

        onUpdate(response);
        setReply(response);

        setDeclining(false);

        showNotification({
          message: 'Отклик отклонен',
          variant: 'success',
        });
      } catch (e) {
        setDeclining(false);
      }
    },
    [onUpdate, propsReply.id, showNotification]
  );

  const onClickAccept = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      try {
        e.preventDefault();
        e.stopPropagation();

        setAccepting(true);

        const response = await acceptReply(propsReply.id);

        onUpdate(response);
        setReply(response);

        setAccepting(false);

        showNotification({
          message: 'Отклик принять',
          variant: 'success',
        });
      } catch (e) {
        setAccepting(false);
      }
    },
    [onUpdate, propsReply.id, showNotification]
  );

  const onClickGoToTender = useCallback(() => {
    if (reply) {
      const path = `/tender/${reply.tenderId}`;
      navigate(path);
    }
  }, [navigate, reply, user.id]);

  const onClickReport = useCallback(() => {
    setReportModalVisible(true);
  }, []);

  const onCloseReportModal = useCallback(() => {
    setReportModalVisible(false);
  }, []);

  const onClickAcceptButton = useCallback(() => {
    if (user.type === UserType.INDIVIDUAL && !user.iin) {
      setModalConfirmVisible(true);
      return;
    }
    setCreateContractModalVisible(true);
  }, [user]);

  const onCloseModalConfirm = useCallback(() => {
    setModalConfirmVisible(false);
  }, []);

  const onClickModalConfirm = useCallback(() => {
    navigate('/profile/edit');
  }, [navigate]);

  const onCloseContractModal = useCallback(() => {
    setCreateContractModalVisible(false);
  }, []);

  const onCloseModalContract = useCallback(() => {
    setContract(null);
    setModalContractVisible(false);
  }, []);

  const onSuccessAddContract = useCallback((doc: Doc) => {
    setCreateContractModalVisible(false);
    setContract(doc);
    setModalContractVisible(true);
  }, []);

  const updateContract = useCallback((data: Partial<Doc>) => {
    setContract((prevContract) => ({
      ...(prevContract as Doc),
      ...data,
    }));
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  const authorCompany = useMemo(() => {
    try {
      return reply?.owner?.companies?.find(
        (val) => val.id === reply?.companyId
      );
    } catch (e) {
      return null;
    }
  }, [reply]);

  const CollapsibleSection = ({
    title,
    isOpen,
    onToggle,
    children,
  }: {
    title: string;
    isOpen: boolean;
    onToggle: () => void;
    children: React.ReactNode;
  }) => (
    <div
      className={cn(
        'border border-gray-200 transition-all duration-200 bg-white',
        isOpen ? 'rounded-lg' : 'rounded-2xl'
      )}
    >
      <button
        onClick={onToggle}
        className={cn(
          'w-full flex items-center justify-between p-4 text-left transition-colors',
          'text-gray-900 hover:bg-gray-50',
          isOpen ? 'rounded-t-lg bg-gray-50' : 'rounded-2xl'
        )}
      >
        <span className="font-medium text-gray-900">{title}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 border-t border-gray-100 rounded-b-lg bg-white">
          {children}
        </div>
      )}
    </div>
  );

  if (pending) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader />
      </div>
    );
  }

  if (error || !reply) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="text-destructive">Ошибка загрузки отклика</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="hidden lg:block">
        <div
          className={cn(
            'grid gap-4',
            reply?.chat?.id ? 'grid-cols-1 lg:grid-cols-5' : 'grid-cols-1'
          )}
        >
          <div className={cn(reply?.chat?.id ? 'lg:col-span-2' : 'col-span-1')}>
            <Paper title="Информация">
              <div className="space-y-4">
                <Info
                  label="Дата подачи"
                  value={dateFormat(propsReply.createdAt)}
                />
                <Info label="Цена" value={currencyFormat(propsReply.price)} />
                <Info label="Комментарий" value={propsReply.description} />
              </div>
            </Paper>

            {reply.requirements && reply.requirements.length > 0 && (
              <Paper title="Требования">
                <div className="max-h-48 overflow-auto space-y-2">
                  {reply.requirements.map((requirement) => (
                    <Info
                      key={requirement.id}
                      icon={
                        requirement.checked
                          ? IconCheckCircle24
                          : IconCloseCircle24
                      }
                      label={requirement.requirement.name}
                      value={requirement.comment || '-'}
                    />
                  ))}
                </div>
              </Paper>
            )}
          </div>

          {reply?.chat?.id && (
            <div className="lg:col-span-3">
              <Chat chatId={reply.chat.id} />
            </div>
          )}
        </div>

        {reply.fileIds && reply.fileIds.length > 0 && (
          <Paper title="Документы и Файлы" />
        )}

        <AuthorSection
          className="page-tender__author"
          author={reply.owner}
          company={authorCompany}
          showContacts={false}
        />
      </div>

      <div className="lg:hidden space-y-4">
        <div className="px-4">
          <AuthorSection
            className="page-tender__author"
            author={reply.owner}
            company={authorCompany}
            showContacts={false}
          />
        </div>

        <div className="space-y-3 px-4">
          <CollapsibleSection
            title="Информация"
            isOpen={isInfoOpen}
            onToggle={() => setIsInfoOpen(!isInfoOpen)}
          >
            <div className="space-y-4 mt-4">
              <Info
                label="Дата подачи"
                value={dateFormat(propsReply.createdAt)}
              />
              <Info label="Цена" value={currencyFormat(propsReply.price)} />
              <Info label="Комментарий" value={propsReply.description} />
            </div>
          </CollapsibleSection>

          {reply.requirements && reply.requirements.length > 0 && (
            <CollapsibleSection
              title="Требования"
              isOpen={isRequirementsOpen}
              onToggle={() => setIsRequirementsOpen(!isRequirementsOpen)}
            >
              <div className="max-h-48 overflow-auto space-y-2 mt-4">
                {reply.requirements.map((requirement) => (
                  <Info
                    key={requirement.id}
                    icon={
                      requirement.checked
                        ? IconCheckCircle24
                        : IconCloseCircle24
                    }
                    label={requirement.requirement.name}
                    value={requirement.comment || '-'}
                  />
                ))}
              </div>
            </CollapsibleSection>
          )}

          {reply?.chat?.id && (
            <CollapsibleSection
              title="Чат"
              isOpen={isChatOpen}
              onToggle={() => setIsChatOpen(!isChatOpen)}
            >
              <div className="mt-4">
                <Chat chatId={reply.chat.id} />
              </div>
            </CollapsibleSection>
          )}

          {reply.fileIds && reply.fileIds.length > 0 && (
            <Paper title="Документы и Файлы" />
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 flex-wrap px-4 lg:px-0 pb-4">
        {reply.status === ReplyStatus.NEW && (
          <>
            {reply.pinned ? (
              <Button
                variant="outline"
                size="sm"
                disabled={accepting || declining || pining}
                onClick={onClickUnpinned}
              >
                <IconFlag className="w-4 h-4 mr-2" />
                Открепить
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                disabled={accepting || declining || pining}
                onClick={onClickPinned}
              >
                <IconFlag className="w-4 h-4 mr-2" />
                Пометить
              </Button>
            )}

            {user.id !== reply.ownerId && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={declining || pining}
                  onClick={onClickDecline}
                >
                  <IconCloseCircle16 className="w-4 h-4 mr-2" />
                  Отклонить
                </Button>
                <Button
                  size="sm"
                  disabled={accepting || pining}
                  onClick={onClickAccept}
                >
                  <IconCheckCircle16 className="w-4 h-4 mr-2" />
                  Принять
                </Button>
              </>
            )}
          </>
        )}
        {/* && !hasContract */}
        {reply.status === ReplyStatus.ACCEPT && (
          <Button
            variant="outline"
            size="sm"
            disabled={declining || pining}
            onClick={onClickAcceptButton}
            className="lg:w-auto w-full"
          >
            <IconCheckCircle16 className="w-4 h-4 mr-2" />
            Заключить договор
          </Button>
        )}

        {user.id !== reply.tender.ownerId && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-md lg:w-auto w-full"
            onClick={onClickGoToTender}
          >
            Перейти в объявление
          </Button>
        )}
      </div>

      {reply && (
        <>
          <ModalReport
            visible={reportModalVisible}
            onClose={onCloseReportModal}
          />
          {/* !hasContract */}
          {reply.status === ReplyStatus.ACCEPT && (
            <>
              <ModalAddPartner
                currentStep={1}
                recipientUser={!reply.companyId ? reply.owner! : null}
                recipientCompany={!!reply.companyId ? reply.company : null}
                visible={createContractModalVisible}
                onClose={onCloseContractModal}
                userType={user.type}
                companyId={user.type === UserType.ENTITY ? company!.id : null}
                onSuccess={onSuccessAddContract}
              />

              <ModalContract
                visible={modalContractVisible}
                updateContract={updateContract}
                contract={contract}
                onClose={onCloseModalContract}
              />

              <ModalConfirm
                onAccept={onClickModalConfirm}
                title={'Заполните ИИН в профиле'}
                textButtonCancel={'Закрыть'}
                textButtonSuccess={'Добавить ИИН в профиль'}
                visible={modalConfirmVisible}
                onClose={onCloseModalConfirm}
                skipSuccessStep={true}
                subText={
                  'Для заключения договоров с партнерами необходимо заполнить ИИН в профиле'
                }
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

export default memo(ReplyDetail);
